import { useState } from "react";

const faqs = [
  {
    id: "how-often",
    question: "How often should I have my gutters cleaned?",
    answer:
      "For most residential properties in Northfleet and Gravesend, we recommend having your gutters cleared at least twice a year — typically in late autumn after leaves have fallen, and again in spring to remove moss and debris that builds up over winter. Properties surrounded by trees, or in areas prone to heavy rainfall, may benefit from quarterly gutter clearance. For commercial properties, the frequency depends on roof size, surrounding vegetation, and drainage system complexity — our team can assess your property and recommend the right maintenance schedule to keep your guttering system in perfect condition year-round.",
  },
  {
    id: "technology",
    question: "What technology do you use to clean gutters?",
    answer:
      "At Mighty Cleaning Services Ltd, we use the latest SkyVac gutter clearance technology — a high-powered industrial vacuum system with carbon fibre poles and a camera head that allows us to clear and inspect gutters from ground level in many cases. This means no unnecessary ladder work, safer working conditions for our team, and a faster, more thorough clear for your property.",
  },
  {
    id: "residential-commercial",
    question: "Do you offer gutter clearance services for both residential and commercial properties?",
    answer:
      "Yes — Mighty Cleaning Services Ltd provides professional gutter clearance for both residential and commercial properties across Northfleet, Gravesend, and the wider Kent area. For homeowners, we offer flexible one-off clears and affordable regular maintenance plans to keep your home protected. For businesses, schools, landlords, and property managers, we provide fully documented commercial gutter clearance contracts with RAMS (Risk Assessment and Method Statements) available on request and flexible scheduling to minimise disruption to your operations. Whether it's a single terraced house or a large commercial estate, our team has the equipment and experience to handle it.",
  },
  {
    id: "signs-blocked",
    question: "What are the signs that my gutters need clearing?",
    answer:
      "The most obvious signs of blocked gutters include water overflowing over the edge of the gutter during rain, damp patches or staining on external walls, visible plant growth or moss inside the guttering, sagging or pulling away from the fascia, and water pooling near your foundations. In some cases, blocked gutters are not visible at all — which is why scheduled maintenance visits from Mighty Cleaning Services Ltd are the best way to stay ahead of problems and protect your property from costly water damage.",
  },
  {
    id: "how-long",
    question: "How long does a gutter clearance take?",
    answer:
      "A standard residential gutter clearance typically takes between 30 minutes and 1.5 hours depending on the size of the property, the amount of debris present, and the accessibility of the guttering system. For commercial properties, times vary depending on the building's footprint and guttering complexity. Our team will give you an estimated time when booking, and we always ensure the job is completed thoroughly — not just quickly. We won't leave until your gutters are clear and we've confirmed everything is completely clean.",
  },
];

export default function ServicesFAQ() {
  const [openId, setOpenId] = useState<string | null>("how-often");

  return (
    <section className="py-24 bg-[#F8FAFE]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
            FAQs
          </span>
          <h2
            className="font-heading text-[#0F2040] leading-tight mb-4"
            style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)", fontWeight: 800 }}
          >
            Frequently Asked Questions About Gutter Clearance
          </h2>
          <p className="text-gray-600 font-body text-base leading-relaxed max-w-xl mx-auto">
            Everything you need to know about our professional gutter clearance and cleaning services in Northfleet, Gravesend, and Kent.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3">
          {faqs.map((faq) => {
            const isOpen = openId === faq.id;
            return (
              <div
                key={faq.id}
                className={`bg-white rounded-xl border transition-all overflow-hidden ${
                  isOpen ? "border-[#1E88E5]/30" : "border-gray-100"
                }`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : faq.id)}
                  className="w-full flex items-center justify-between gap-4 px-7 py-5 text-left cursor-pointer"
                >
                  <span
                    className="font-heading text-[#0F2040] text-sm leading-snug"
                    style={{ fontWeight: isOpen ? 700 : 600 }}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                      isOpen ? "bg-[#1E88E5] text-white" : "bg-[#EBF4FF] text-[#1E88E5]"
                    }`}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    ></i>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-7 pb-6 border-t border-gray-50 pt-4">
                    <p className="font-body text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom prompt */}
        <div className="mt-12 text-center">
          <p className="font-body text-sm text-gray-500 mb-4">
            Still have questions about gutter clearance in Northfleet or Gravesend?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:02084322244"
              className="flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call 0208 43 222 44
            </a>
            <a
              href="#quote-form"
              className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1E88E5] text-[#1E88E5] font-heading font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Send Us a Message
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
